import React, { useRef } from "react";
import { useState, useEffect } from "react";

import {
  SpaceBetween,
  Header,
  Container,
  Box,
  Icon,
  FormField,
  Select,
  SelectProps,
  Button,
  Grid,
  Link,
  Alert,
} from "@amzn/awsui-components-console";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import type { RootState } from "../../redux/store";
import moment from "moment";
import Spinner from "@amzn/awsui-components-console/spinner";
import { MetricsPublisher } from "src/metrics";
import SimLinkGenerator from "src/components/simLinkGenerator/SimLinkGenerator";
import { SIM_TYPES } from "src/components/simLinkGenerator/types";
import { PAGE_TITLES } from "src/app/constants";
import {
  getFile,
  listFiles,
  setValueInWorkersCompensationDocumentsState,
} from "./workersCompensationDocumentsSlice";
import {
  MEDICAL_PANEL_DOCUMENT_TYPE,
  US_WC_RIVER_WORKFLOW_URL,
} from "src/views/workersCompensationDocuments/constants";
import { ClaimFile } from "@amzn/ttechclaimintakeservice-client";
import RetrievingFileList from "./RetrievingFileList";
import { FileOption } from "./types";
import { convertClaimFilesResponseToOptions } from "./utils";

const RIVER_WORKFLOW_LINK_ARIA_LABEL =
  "Pressing this link will open the US Workers' Compensation River workflow in a new tab, where you can submit a request to the US Workers' Compensation team.";

enum FILE_ORIGIN {
  MATCHING_SITE = "matchingSite",
  SELECTED_SITE = "selectedSite",
}
//Declare metricsPublishers, they will be initialized on component mount
let metricsPublisher: MetricsPublisher;
let metricsPublisherVisitedBySite: MetricsPublisher;
let metricsPublisherNoMatchFound: MetricsPublisher;
let metricsPublisherDownloadsBySite: MetricsPublisher;
let metricsPublisherNonMatchingDownload: MetricsPublisher;
const MedicalPanelData = () => {
  /**
   * Setup Redux variables and get state
   */

  //Redux Auth state
  const { user } = useAppSelector((state: RootState) => state.auth);

  //Redux MedicalPanelData state
  const { files, fileUrl, gettingFiles, gettingFileUrl, listFilesError, getFileUrlError } =
    useAppSelector(
      (state: RootState) => state.workersCompensationDocuments[MEDICAL_PANEL_DOCUMENT_TYPE]
    );

  const dispatch = useAppDispatch();
  const [fileOptions, setFileOptions] = useState<FileOption[]>([]);
  const [selectedOption, setSelectedOption] = useState<FileOption | null>(null);
  const [matchingFile, setMatchingFile] = useState<ClaimFile | null>(null);
  const [alert, setAlert] = useState<React.ReactNode | null>(null);
  const [fileUrlOrigin, setFileUrlOrigin] = useState<FILE_ORIGIN | null>(null);

  const userLoaded = useRef(false);
  /**
   * Get file list on load
   */
  useEffect(() => {
    metricsPublisher = new MetricsPublisher("medicalPanelData");
    metricsPublisherVisitedBySite = new MetricsPublisher("medicalPanelDataVisitedBySite");
    metricsPublisherNoMatchFound = new MetricsPublisher("medicalPanelDataNoMatchFound");
    metricsPublisherDownloadsBySite = new MetricsPublisher("medicalPanelDataDownloadsBySite");
    metricsPublisherNonMatchingDownload = new MetricsPublisher(
      "medicalPanelDataNonMatchingDownload"
    );
    dispatch(listFiles({ fileType: MEDICAL_PANEL_DOCUMENT_TYPE }));

    metricsPublisher.publishMedicalPanelDataVisited();
  }, []);
  /**
   * Publish metric medicalPanelDataVisitedBySite when user is loaded
   */
  useEffect(() => {
    if (user?.siteCode && !userLoaded.current) {
      userLoaded.current = true;
      metricsPublisherVisitedBySite.publishMedicalPanelDataVisitedBySite(user.siteCode);
    }
  }, [user.siteCode]);
  /**
   * Search files for matching site
   */
  useEffect(() => {
    const filtered = user?.siteCode
      ? files.filter((file: ClaimFile) => {
          // Splitting fileName to isolate site code
          const splitFileName = file.fileName?.toLowerCase().split(/[-._\s/:,#]+/);
          return splitFileName?.includes(user.siteCode.toLowerCase());
        })
      : [];

    const options = convertClaimFilesResponseToOptions(files);
    /**
     * Sort options alphabetically based on label.
     * File names sorting is being handled in the Front-end and not in the Back-end
     * because file names are already being processed here and it will keep the
     * data processing step consistent.
     */
    options.sort((a: FileOption, b: FileOption) => {
      return a.label.localeCompare(b.label);
    });
    setFileOptions(options);
    if (filtered?.length) {
      metricsPublisher.publishMedicalPanelDataMatchSuccess();

      // Sort filenames based on upload timestamp in descending order
      filtered.sort((a, b) => {
        if (!a.lastUploadTimestamp || !b.lastUploadTimestamp) {
          return 0;
        }
        return b.lastUploadTimestamp - a.lastUploadTimestamp;
      });

      // Set file to latest file found from the list
      setMatchingFile(filtered[0]);
    } else if (user?.siteCode) {
      metricsPublisherNoMatchFound.publishMedicalPanelDataMatchNotFound(user.siteCode);
    }
    metricsPublisher.publishLoadMedicalPanelDataPageLatency();
  }, [files]);

  /**
   * Trigger file download when url is received
   */
  useEffect(() => {
    if (fileUrl) {
      window.open(fileUrl, "_blank");
      dispatch(
        setValueInWorkersCompensationDocumentsState({
          key: "fileUrl",
          value: "",
          fileType: MEDICAL_PANEL_DOCUMENT_TYPE,
        })
      );
      metricsPublisher.publishMedicalPanelDataDownloads();

      if (user?.siteCode) {
        metricsPublisherDownloadsBySite.publishMedicalPanelDataDownloadsBySite(user.siteCode);
        // Check if downloaded file does not match user's site and publish metric: ${siteName}-${fileName}: count
        if (
          fileUrlOrigin === FILE_ORIGIN.SELECTED_SITE &&
          !selectedOption?.label.toLowerCase().includes(user.siteCode.toLowerCase()) &&
          matchingFile
        ) {
          const fileNameWithNoExtension =
            selectedOption?.label?.split(".")[0]?.replace(/\s+/g, "_") || "";
          const siteNameAndFileName = `${user.siteCode}-${fileNameWithNoExtension}`;
          metricsPublisherNonMatchingDownload.publishMedicalPanelDataNonMatchingDownload(
            siteNameAndFileName
          );
        }
      }
    }
  }, [fileUrl]);

  /**
   * Set alert if there was an error getting the fileUrl
   */
  useEffect(() => {
    if (getFileUrlError) {
      setAlert(
        <Box data-testid="getFileUrlErrorAlert">
          There was a problem retrieving the download link for this file, please try again later,
          reload the page or search the documents below for another file, if the problem persists
          please open a{" "}
          <SimLinkGenerator
            simType={SIM_TYPES.REPORT_A_BUG}
            target="_blank"
            ariaLabel="Pressing this link will open a new tab where you will be able to create a ticket for the team to investigate this issue."
          >
            support ticket
          </SimLinkGenerator>{" "}
          to assist you with this issue.
        </Box>
      );
    } else {
      setAlert(null);
    }
  }, [getFileUrlError]);

  const getFileUrl = (fileName?: string) => {
    if (!fileName) return;
    setAlert(null);
    dispatch(getFile({ fileName, fileType: MEDICAL_PANEL_DOCUMENT_TYPE }));
  };
  const renderMatchFoundMessage = () => {
    let result = (
      <Box variant="p" color="text-status-warning" data-testid="noMatchingFileWarning">
        <Icon name="status-warning"></Icon> There was no medical provider information found for your
        site, please submit a request through the{" "}
        <Link
          href={US_WC_RIVER_WORKFLOW_URL}
          target="_blank"
          variant="primary"
          ariaLabel={RIVER_WORKFLOW_LINK_ARIA_LABEL}
        >
          US Workers&apos; Compensation River workflow
        </Link>
        . If you want to search medical provider information for another site, please use the
        dropdown menu below.
      </Box>
    );
    if (matchingFile) {
      const tokenizedFileName = matchingFile.fileName?.split("/") ?? [];
      const standaloneFileName = tokenizedFileName[tokenizedFileName.length - 1];
      result = (
        <SpaceBetween size="xs" direction="vertical">
          <Box variant="p" color="text-status-success" data-testid="matchingFileSuccess">
            <Icon name="status-positive"></Icon> Click on the download link below to get the medical
            provider information for your site or search for another site&apos;s medical provider
            information in the dropdown menu.
          </Box>
          <Box variant="p">
            <b>File: </b>
            <span
              data-testid="matchingFileDownloadLink"
              onClick={() => {
                setFileUrlOrigin(FILE_ORIGIN.MATCHING_SITE);
                getFileUrl(matchingFile.fileName);
              }}
            >
              <Link variant="primary">
                {`${standaloneFileName} `} <Icon name="download"></Icon>
              </Link>
            </span>
            {gettingFileUrl && fileUrlOrigin === FILE_ORIGIN.MATCHING_SITE ? <Spinner /> : null}
            <br />
            <span>
              <b>Date Uploaded: </b>
              {`${moment(matchingFile.lastUploadTimestamp).format("LLLL")}`}
            </span>
          </Box>
        </SpaceBetween>
      );
    }
    return result;
  };

  const renderErrorListingFiles = () => {
    return (
      <SpaceBetween direction="vertical" size="xs" data-testid="listingFilesErrorAlertContainer">
        <Alert type="error" data-testid="listingFilesErrorAlert">
          There was an error retrieving the files. Please reload the page or click on the button
          below to try again. If the error persists please open a{" "}
          <SimLinkGenerator
            simType={SIM_TYPES.REPORT_A_BUG}
            target="_blank"
            ariaLabel="Pressing this link will open a new tab where you will be able to create a ticket for the team to investigate this issue."
          >
            support ticket
          </SimLinkGenerator>{" "}
          to get help with this issue.
        </Alert>
        <Button
          variant="normal"
          onClick={() => {
            dispatch(listFiles({ fileType: MEDICAL_PANEL_DOCUMENT_TYPE }));
          }}
        >
          Reload File List
        </Button>
      </SpaceBetween>
    );
  };

  const renderErrorAlert = () => {
    if (alert) {
      return (
        <Alert type="error" data-testid="medicalPanelDataAlert">
          {alert}
        </Alert>
      );
    }
  };
  const renderInfoBanner = () => {
    return (
      <Alert type="info">
        <SpaceBetween direction="vertical" size="xs">
          <Box>
            Note:
            <br />
            <SpaceBetween direction="vertical" size="xs">
              <span>
                Medical provider information is available for most sites and jurisdictions to assist
                associates in seeking medical care, even if not required.
              </span>
              <span>
                The following states are <b className="text-underline">required</b> by state law to
                display medical panel in the common areas within the building: CO, GA, PA, TN, VA.
              </span>
            </SpaceBetween>
          </Box>
          <Box>
            If you are unable to find the medical provider information for the site you were
            searching for or have any questions related to medical provider information, please
            submit a request through the{" "}
            <Link
              href={US_WC_RIVER_WORKFLOW_URL}
              target="_blank"
              variant="primary"
              ariaLabel={RIVER_WORKFLOW_LINK_ARIA_LABEL}
            >
              US Workers&apos; Compensation River workflow
            </Link>
            .
          </Box>
        </SpaceBetween>
      </Alert>
    );
  };

  return (
    <SpaceBetween direction="vertical" size="s">
      <Container header={<Header variant="h2">{PAGE_TITLES.MEDICAL_PANEL_DATA}</Header>}>
        <SpaceBetween direction="vertical" size="s">
          {renderInfoBanner()}
          {listFilesError ? (
            renderErrorListingFiles()
          ) : gettingFiles ? (
            <RetrievingFileList />
          ) : (
            <SpaceBetween size="s" direction="vertical">
              {renderErrorAlert()}
              {renderMatchFoundMessage()}

              <FormField
                description="Search from the dropdown menu using keywords related to the site for which you want to download the medical provider data file from"
                label="Amazon sites medical provider search"
                data-testid="medicalPanelFilesDropdownFormField"
              >
                <Grid gridDefinition={[{ colspan: 8 }, { colspan: 4 }]}>
                  <Select
                    data-testid="medical-panel-dropdown"
                    options={fileOptions}
                    selectedOption={selectedOption}
                    onChange={({ detail }: { detail: SelectProps.ChangeDetail }) => {
                      setSelectedOption(detail.selectedOption as FileOption);
                    }}
                    filteringType="auto"
                    placeholder="Select a file to download"
                    virtualScroll={true}
                  ></Select>
                  <Button
                    data-testid="medical-panel-download-btn"
                    disabled={!selectedOption || gettingFileUrl}
                    variant="primary"
                    onClick={() => {
                      setFileUrlOrigin(FILE_ORIGIN.SELECTED_SITE);
                      getFileUrl(selectedOption?.value);
                    }}
                    loading={gettingFileUrl && fileUrlOrigin === FILE_ORIGIN.SELECTED_SITE}
                  >
                    Download
                  </Button>
                </Grid>
                {selectedOption ? (
                  <div>
                    <span>
                      <b>Date Uploaded: </b>
                      {`${moment(selectedOption.lastUploadTimestamp).format("LLLL")}`}
                      {}
                    </span>
                  </div>
                ) : null}
              </FormField>
            </SpaceBetween>
          )}
        </SpaceBetween>
      </Container>
    </SpaceBetween>
  );
};
export default MedicalPanelData;
